import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { cn } from "../../../utils";
import { Button } from "../../base/button/button";
import { Popover, PopoverAnchor, PopoverContent } from "../../base/popover/popover";
import { Textarea } from "../../base/textarea/textarea";
import { MAX_AI_FEEDBACK_COMMENT_LENGTH } from "@bliks-io/contracts";
import { useLatestRef } from "../../../hooks/useLatestRef";
const THUMB_SIZE = "size-6 [&_svg]:size-3.5";
/**
 * A cast vote has to be VISIBLE, not merely announced: `aria-pressed` alone
 * leaves a voted thumb pixel-identical to an unvoted one, so a sighted reader
 * cannot tell what they (or, on a shared Bliksy recording, a colleague) already
 * said. The active thumb fills its icon — the `score-badge` treatment — and
 * takes the full foreground tone against the muted resting one; both tokens are
 * theme-aware, so the pair reads in light and dark alike.
 */ const ACTIVE_THUMB = {
    button: `${THUMB_SIZE} text-foreground`,
    icon: "fill-current"
};
const INACTIVE_THUMB = {
    button: `${THUMB_SIZE} text-muted-foreground`,
    icon: undefined
};
function thumbClasses(active) {
    return active ? ACTIVE_THUMB : INACTIVE_THUMB;
}
/**
 * The thumbs row under an AI answer (plan 021 PR-1).
 *
 * Pressing thumbs-down votes IMMEDIATELY and opens the comment popover on top
 * of the recorded vote: dismissing it leaves a comment-less down-vote standing,
 * so a reader who cannot be bothered to explain still gets counted. Pressing the
 * thumb that is already active retracts the vote. Thumbs-up takes no comment
 * (owner ruling) — praise needs no justification.
 */ export function ChatFeedbackBlock({ messageId, userFeedback, onFeedback }) {
    const { t } = useTranslation();
    const storedVote = userFeedback?.value ?? null;
    // State-for-prev rather than an effect: under StrictMode a ref advanced during
    // the discarded render pass makes the committed pass believe nothing changed
    // and skip the reseed, stranding the widget on the previous vote.
    const [seededVote, setSeededVote] = useState(storedVote);
    const [vote, setVote] = useState(storedVote);
    if (seededVote !== storedVote) {
        setSeededVote(storedVote);
        setVote(storedVote);
    }
    // The rollback target is read when the submission SETTLES, not when it was
    // pressed: a re-seed landing mid-flight — a colleague's verdict arriving on a
    // shared Bliksy recording — is newer information than the vote this press
    // replaced, and undoing it would put a stale verdict back on screen.
    const latestStoredVote = useLatestRef(storedVote);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingComment, setIsSendingComment] = useState(false);
    const [isCommentOpen, setIsCommentOpen] = useState(false);
    const [comment, setComment] = useState("");
    const downButtonRef = useRef(null);
    const dismissedFromOutside = useRef(false);
    /** Whether the vote landed — the popover's fate hangs on it. */ const submit = async (value, submittedComment)=>{
        const previousVote = vote;
        const storedVoteAtPress = latestStoredVote.current;
        setVote(value);
        setIsSubmitting(true);
        try {
            await onFeedback({
                messageId,
                value,
                ...submittedComment ? {
                    comment: submittedComment
                } : {}
            });
            return true;
        } catch  {
            const reseeded = latestStoredVote.current !== storedVoteAtPress;
            setVote(reseeded ? latestStoredVote.current : previousVote);
            return false;
        } finally{
            setIsSubmitting(false);
        }
    };
    // Both thumbs are disabled while a submission is in flight (the comment box is
    // not — it collects text for a vote that has already landed), so a down-vote
    // and its comment can never reach the server out of order.
    const pressThumb = async (pressed)=>{
        const opensComment = pressed === "down" && vote !== "down";
        if (opensComment) setComment("");
        setIsCommentOpen(opensComment);
        const submitted = await submit(vote === pressed ? null : pressed);
        // A comment box hovering over a down-vote that never landed would collect
        // text for a vote the server has never heard of.
        if (opensComment && !submitted) setIsCommentOpen(false);
    };
    const sendComment = async ()=>{
        const written = comment.trim();
        if (!written) {
            setIsCommentOpen(false);
            return;
        }
        // Kept open — with the text intact — when the send fails, so the reader can
        // retry instead of retyping what they already wrote.
        setIsSendingComment(true);
        const submitted = await submit("down", written);
        setIsSendingComment(false);
        if (submitted) setIsCommentOpen(false);
    };
    const thumbsUpLabel = t("chat.feedback.thumbsUp");
    const thumbsDownLabel = t("chat.feedback.thumbsDown");
    const commentTitle = t("chat.feedback.commentTitle");
    const upThumb = thumbClasses(vote === "up");
    const downThumb = thumbClasses(vote === "down");
    // The row stays out of the way until the reader hovers the turn (the
    // `group/message` scope of the shadcn Message primitive) or tabs onto a
    // thumb. A standing vote or an open comment box keeps it on screen — a cast
    // verdict must stay visible — and coarse pointers have no hover to reveal
    // it with, so they always see it.
    const revealOnHoverOnly = vote === null && !isCommentOpen;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn("flex items-center gap-1 transition-opacity", revealOnHoverOnly && "opacity-0 group-hover/message:opacity-100 focus-within:opacity-100 any-pointer-coarse:opacity-100"),
        children: [
            /*#__PURE__*/ _jsx(Button, {
                type: "button",
                variant: "ghost",
                size: "icon-sm",
                className: upThumb.button,
                "aria-label": thumbsUpLabel,
                title: thumbsUpLabel,
                "aria-pressed": vote === "up",
                disabled: isSubmitting,
                onClick: ()=>void pressThumb("up"),
                children: /*#__PURE__*/ _jsx(ThumbsUp, {
                    "aria-hidden": true,
                    className: upThumb.icon
                })
            }),
            /*#__PURE__*/ _jsxs(Popover, {
                open: isCommentOpen,
                onOpenChange: setIsCommentOpen,
                children: [
                    /*#__PURE__*/ _jsx(PopoverAnchor, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx(Button, {
                            ref: downButtonRef,
                            type: "button",
                            variant: "ghost",
                            size: "icon-sm",
                            className: downThumb.button,
                            "aria-label": thumbsDownLabel,
                            title: thumbsDownLabel,
                            "aria-pressed": vote === "down",
                            "aria-haspopup": "dialog",
                            "aria-expanded": isCommentOpen,
                            disabled: isSubmitting,
                            onClick: ()=>void pressThumb("down"),
                            children: /*#__PURE__*/ _jsx(ThumbsDown, {
                                "aria-hidden": true,
                                className: downThumb.icon
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(PopoverContent, {
                        align: "start",
                        className: "flex flex-col gap-2",
                        onInteractOutside: ()=>{
                            dismissedFromOutside.current = true;
                        },
                        // The popover has no `PopoverTrigger` to hand focus back to (the down
                        // button votes rather than toggles), so Radix would drop focus on the
                        // document body when it closes. A dismissal by clicking elsewhere is
                        // the exception: whatever was clicked — the other thumb, the composer
                        // — must keep the focus it just took.
                        onCloseAutoFocus: (event)=>{
                            event.preventDefault();
                            if (!dismissedFromOutside.current) downButtonRef.current?.focus();
                            dismissedFromOutside.current = false;
                        },
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm font-medium",
                                children: commentTitle
                            }),
                            /*#__PURE__*/ _jsx(Textarea, {
                                value: comment,
                                onChange: (event)=>setComment(event.target.value),
                                maxLength: MAX_AI_FEEDBACK_COMMENT_LENGTH,
                                placeholder: t("chat.feedback.commentPlaceholder"),
                                "aria-label": commentTitle
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "small",
                                className: "self-end",
                                loading: isSendingComment,
                                disabled: isSubmitting,
                                onClick: ()=>void sendComment(),
                                children: t("chat.feedback.commentSend")
                            })
                        ]
                    })
                ]
            })
        ]
    });
}

//# sourceMappingURL=chat-feedback.js.map